import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'

/**
 * BentoGrid - Conteneur de grille Bento flexible
 *
 * Utilise le système de grille 12 colonnes de SERGE avec support complet
 * pour des layouts asymétriques et responsives.
 *
 * @example
 * <BentoGrid>
 *   <BentoItem colSpan={8}>Main content</BentoItem>
 *   <BentoItem colSpan={4}>Sidebar</BentoItem>
 * </BentoGrid>
 */
export interface BentoGridProps extends HTMLAttributes<HTMLDivElement> {
  /** Éléments de la grille (BentoItem) */
  children: ReactNode
}

export const BentoGrid = forwardRef<HTMLDivElement, BentoGridProps>(
  ({ children, className = '', ...rest }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('serge-bento-grid', className)}
        {...rest}
      >
        {children}
      </div>
    )
  }
)

BentoGrid.displayName = 'BentoGrid'

/**
 * BentoItem - Élément de grille avec support column/row span
 *
 * Permet de définir la taille et la position d'un élément dans la grille.
 * Supporte le spanning vertical (row-span) pour des layouts complexes.
 *
 * @example
 * <BentoItem colSpan={6} rowSpan={2}>
 *   Featured content spanning 2 rows
 * </BentoItem>
 */
export interface BentoItemProps extends HTMLAttributes<HTMLDivElement> {
  /** Nombre de colonnes à occuper (1-12) */
  colSpan?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  /** Nombre de lignes à occuper (1-4) */
  rowSpan?: 1 | 2 | 3 | 4
  /** Position de départ (1-7) */
  colStart?: 1 | 2 | 3 | 4 | 5 | 6 | 7
  /** Contenu de l'élément */
  children: ReactNode
}

export const BentoItem = forwardRef<HTMLDivElement, BentoItemProps>(
  ({ colSpan = 4, rowSpan = 1, colStart, children, className = '', ...rest }, ref) => {
    const classes = clsx(
      colSpan && `serge-col-${colSpan}`,
      rowSpan > 1 && `serge-row-${rowSpan}`,
      colStart && `serge-col-start-${colStart}`,
      className
    )

    return (
      <div ref={ref} className={classes} {...rest}>
        {children}
      </div>
    )
  }
)

BentoItem.displayName = 'BentoItem'
